import { Outlet, createFileRoute, linkOptions } from '@tanstack/react-router'
import { useState } from 'react'
import type {
  InsistenceFilters,
  InsistenceResponse,
  InsistenceSortingState,
} from 'types'

import StreamingExportDialog, {
  ExportType,
} from '@/components/export/streaming-export-dialog'
import SearchBar from '@/components/search-bar'
import type { CursorPaginationState } from '@/components/table/cursor-pagination'
import { InsistencesColumn } from '@/components/tables/insistences/insistences-columns.enum'
import InsistencesTable from '@/components/tables/insistences/insistences-table'
import { DEFAULT_PAGE_SIZE } from '@/lib/table'

export const Route = createFileRoute(
  '/app/data/companies/$uuid/(mainPage)/insured/insistences',
)({
  component: RouteComponent,
})

const insistenceRowLink = (_insistence: InsistenceResponse) =>
  linkOptions({
    from: Route.fullPath,
    to: '/app/data/companies/$uuid/insured/insistences',
    viewTransition: false,
  })

const excludedColumns: InsistencesColumn[] = [InsistencesColumn.InsuredCompany]

function RouteComponent() {
  const { uuid: insuredCompanyUuid } = Route.useParams()
  const [filters, setFilters] = useState<InsistenceFilters>({
    insuredCompanyUuid,
  })
  const [pagination, setPagination] = useState<CursorPaginationState>({
    cursor: undefined,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [sorting, setSorting] = useState<InsistenceSortingState>([])

  const handleFiltersChange = (
    updater: (filters: InsistenceFilters) => InsistenceFilters,
  ) => {
    setFilters((previous) => ({
      ...updater(previous),
      insuredCompanyUuid,
    }))

    // reset pagination to the first page on filter change
    setPagination((previous) => ({ ...previous, cursor: undefined }))
  }

  const handleSearchChange = (search: string) =>
    handleFiltersChange((previous) => ({ ...previous, search }))

  return (
    <div className="flex h-full flex-col justify-between gap-4 overflow-hidden">
      <div className="flex items-center justify-between gap-2">
        <SearchBar
          tabIndex={1}
          text={filters.search}
          setText={handleSearchChange}
        />
        <StreamingExportDialog
          type={ExportType.Insistences}
          filters={filters}
          buttonVariant="outlineGray"
        />
      </div>
      <InsistencesTable
        filters={filters}
        setFilters={handleFiltersChange}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        rowLink={insistenceRowLink}
        excludedColumns={excludedColumns}
      />
      <Outlet />
    </div>
  )
}
